<template>
    <ul class="mead-sidebar"
        role="list"
        :class="[
            type === 'horizontal' ? 'el--t-horizontal' : '',
            type === 'pills' ? 'el--t-pills' : '',
            standalone ? 'el--standalone' : '']">
        <li class="mead-sidebar-el"
            v-for="item in items"
            :key="item.uid"
            :qa="qaTags[item.uid]"
            role="button"
            :tabindex="item.disabled ? -1 : 0"
            :aria-disabled="item.disabled ? 'true' : null"
            :aria-current="item.uid === selectedItem ? 'true' : null"
            :aria-label="translate(item.label)"
            :class="[item.uid === selectedItem ? 'el--active' : '', item.disabled ? 'el--disabled' : '']"
            @click="handleSelect(item)"
            @keydown.enter="handleSelect(item)"
            @keydown.space.prevent="handleSelect(item)">
            <Icon v-if="item.icon" :name="item.icon" ui-size="l" class="sm-r-s" />
            <slot name="item" :item="item">
                <span class="mead-sidebar-el-label">{{ item.label | athT }}</span>
            </slot>
            <Badge v-if="item.badge !== undefined && item.badge !== null && item.badge !== ''" :lbl="item.badge" />
        </li>
    </ul>
</template>
<script>
import { isNeString } from '@aretheyhappy/utils';
import Badge from '../atoms/visuals/Badge.vue';
import Icon from '../atoms/visuals/Icon.vue';

export default {
    name: 'mead-sidebar',
    components: {
        Badge,
        Icon,
    },
    props: {
        /**
         * The list of items to render as sidebar entries. Each item is an object
         * with a unique `uid` (used as the render key and matched against
         * `selectedItem` to determine the highlighted/active state) and a `label`
         * (passed through the athT translation filter). Optional per-item fields:
         * `icon` (a linearicons name rendered before the label via the icon
         * component), `disabled` (greys the item out and blocks selection), and
         * `badge` (a count/label rendered at the end of the item via the badge
         * component, e.g. an unread count), and `qa` (a Playwright test tag for
         * the item; defaults to `sidebar-item-{label}` when not provided).
         */
        items: {type: Array, default: () => []},
        /**
         * The `uid` of the currently-selected item. The item whose `uid` matches
         * this value gets the `el--active` highlighted state. Selection is fully
         * controlled by the consumer.
         */
        selectedItem: {type: [String, Number], default: null},
        /**
         * Layout style of the sidebar. 'vertical' stacks items in a column,
         * 'horizontal' lays them out in a row, and 'pills' renders them as a
         * compact row of pill-shaped tabs.
         */
        type: {type: String, default: 'vertical', validator: value => ['vertical', 'horizontal', 'pills'].includes(value)},
        /**
         * Only relevant when type is 'pills'. Gives the pill row its own
         * background/border so it reads as a standalone control.
         */
        standalone: {type: Boolean, default: false},
    },
    computed: {
        /**
         * Map of item uid to its qa tag. Uses the item's own `qa` when provided,
         * otherwise falls back to `sidebar-item-{label}`.
         */
        qaTags() {
            const tags = {};
            for (const item of this.items) {
                if (isNeString(item.qa)) tags[item.uid] = item.qa;
                else if (isNeString(item.label)) tags[item.uid] = `sidebar-item-${item.label}`;
                else tags[item.uid] = null;
            }
            return tags;
        }
    },
    methods: {
        translate(text) {
            return isNeString(text) ? MeadCommon.I18N.go(text) : text;
        },
        handleSelect(item) {
            if (item.disabled) return;

            this.$emit('onSelect', item);
        }
    }
};
</script>
<style lang="scss" scoped>
@import '../../../sass/_variables.scss';

.mead-sidebar-el {
    border: 1px solid transparent;
    border-radius: 0;
    padding: $s-s;
    cursor: pointer;
    min-height: 4rem;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    max-width: 100%;

    &:not(.el--active) {
        &:not(:first-of-type) {
            border-top-color: var(--c-panel-border);
        }

        &:hover,
        &:focus-visible {
            background-color: var(--c-brand-light-bg);
            color: var(--c-brand-light-fg);
            border-radius: $br-s;

            &,
            & + .mead-sidebar-el {
                border-top-color: transparent;
            }
        }
    }

    &:focus-visible {
        outline: 2px solid var(--c-panel-border-active);
        outline-offset: -2px;
    }

    & > .mead-sidebar-el-label {
        flex-grow: 1;
        font-size: $fs-m;
        font-family: $ff-h;
        text-overflow: ellipsis;

        & + span {
            margin-left: $s-s;
        }
    }

    //  MODIFIERS

    &.el--active {
        background-color: var(--c-panel-bg);
        border-radius: $br-s;
        box-shadow: 0 0 0 1px var(--c-panel-border-active), var(--c-shade-m);
        z-index: 1;

        & > .mead-sidebar-el-label {
            font-weight: bold;
        }
    }

    &.el--disabled {
        cursor: not-allowed;
        filter: grayscale(1);
        opacity: .7;
    }
}

.mead-sidebar {
    display: flex;
    flex-direction: column;
    position: relative;

    &.el--t-horizontal {
        flex-direction: row;
        display: inline-flex;

        .mead-sidebar-el {
            width: auto;

            &.el--active:only-child {
                box-shadow: none;
                border-radius: 0;
            }

            &:not(.el--active) {
                &:not(:first-of-type) {
                    border-left-color: var(--c-panel-border);
                    border-top-color: transparent;
                }

                &:hover:not(:only-child) {
                    &,
                    & + .mead-sidebar-el {
                        border-left-color: transparent;
                        border-top-color: transparent;
                    }
                }
            }
        }
    }

    &.el--t-pills {
        flex-direction: row;
        display: inline-flex;
        border-radius: $br-m;
        overflow: hidden;
        padding: $s-xs;
        border: 1px solid var(--c-panel-border);
        background-color: var(--c-panel-bg);

        .mead-sidebar-el {
            box-shadow: none;
            border-width: 0;
            padding: 0 $s-s;
            min-height: initial;
            line-height: 3rem;

            &.el--active {
                background-color: var(--c-brand-active-bg);
                color: var(--c-brand-active-fg);
                box-shadow: var(--c-shade-s);
            }
        }

        &.el--standalone {
            background-color: var(--c-brand-light-bg);
            border-radius: $br-m;
            border: none;

            .mead-sidebar-el {
                border-radius: $br-m;
            }
        }
    }
}
</style>
